import {
  COMPANY_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  getCompanyOptionsForEmploymentType as getStaticCompanyOptionsForEmploymentType
} from '../constants/employmentOptions.js';
import {
  findTypeByCode,
  getCatalogCompanyOptionsForEmploymentType,
  getCatalogEmploymentTypeOptions,
  getAllCatalogCompanySelectOptions
} from '../composables/employmentReferenceCatalogState.js';

/** Legacy import labels → current catalog names. */
const EMPLOYING_COMPANY_CATALOG_ALIASES = Object.freeze({
  'CRC Transport': 'CRC Transport LLC',
  'CRC Auto Group LLC': 'CRC Auto Group'
});

/**
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function normalizeEmployingCompanyName(name) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '';
  return EMPLOYING_COMPANY_CATALOG_ALIASES[trimmed] || trimmed;
}

/** @param {string|null|undefined} type */
export function getCompanyOptionsForEmploymentType(type) {
  const t = String(type || '').trim();
  if (!t) return [];
  if (isLegacyEmploymentType(t)) {
    return getAllCatalogCompanySelectOptions();
  }
  const catalogOptions = getCatalogCompanyOptionsForEmploymentType(type);
  if (catalogOptions.length) return catalogOptions;
  return getStaticCompanyOptionsForEmploymentType(type);
}

/** @returns {Array<{label: string, value: string}>} */
export function getEmploymentTypeOptions() {
  const catalogOptions = getCatalogEmploymentTypeOptions();
  if (catalogOptions.length) {
    return catalogOptions.map(({ label, value }) => ({ label, value }));
  }
  return EMPLOYMENT_TYPE_OPTIONS.map((row) => ({ ...row }));
}

const EMPLOYMENT_TYPE_LABELS = {
  W2: 'W2',
  '1099': '1099',
  CIM: 'CIM',
  full_time: 'Full time',
  part_time: 'Part time',
  contractor: 'Contractor',
  temporary: 'Temporary',
  intern: 'Intern',
  other: 'Other'
};

/**
 * @param {string} value
 * @returns {string}
 */
function humanizeSnakeCase(value) {
  return String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isLegacyEmploymentType(value) {
  const v = String(value || '').trim();
  if (!v) return false;
  return !getEmploymentTypeOptions().some((option) => option.value === v);
}

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function getEmploymentTypeDisplayLabel(value) {
  if (value == null || value === '') return '';
  const key = String(value).trim();
  const catalog = findTypeByCode(key);
  if (catalog?.label) return catalog.label;
  if (EMPLOYMENT_TYPE_LABELS[key]) return EMPLOYMENT_TYPE_LABELS[key];
  return humanizeSnakeCase(key);
}

/**
 * Single source for table, profile, review, edit display.
 * @param {string|null|undefined} value
 * @param {{ empty?: string }} [opts]
 * @returns {string}
 */
export function formatEmploymentType(value, opts = {}) {
  const empty = opts.empty ?? '—';
  if (value == null || value === '') return empty;
  return getEmploymentTypeDisplayLabel(value) || empty;
}

/**
 * @param {string|null|undefined} companyName
 * @param {string|null|undefined} type
 * @returns {boolean}
 */
export function isCompanyAllowedForEmploymentType(companyName, type) {
  const name = normalizeEmployingCompanyName(companyName);
  if (!name) return true;
  if (isLegacyEmploymentType(type)) {
    return getAllCatalogCompanySelectOptions().some((option) => option.value === name);
  }
  const allowed = getCompanyOptionsForEmploymentType(type);
  return allowed.some((c) => c.value === name);
}

/**
 * Build employment type Select options; injects a legacy option for the current record only.
 * @param {string|null|undefined} currentValue
 * @returns {Array<{label: string, value: string, legacy?: boolean}>}
 */
export function buildEmploymentTypeSelectOptions(currentValue) {
  const options = getEmploymentTypeOptions().map((o) => ({ ...o }));
  const current = String(currentValue || '').trim();
  if (!current) return options;
  if (options.some((o) => o.value === current)) return options;
  const label = `${getEmploymentTypeDisplayLabel(current)} — current legacy value`;
  return [{ label, value: current, legacy: true }, ...options];
}

/**
 * Build company Select options for a type; preserves custom legacy company for the current record.
 * @param {string|null|undefined} type
 * @param {string|null|undefined} currentCompanyName
 * @returns {Array<{label: string, value: string, legacy?: boolean}>}
 */
export function buildCompanySelectOptions(type, currentCompanyName) {
  const options = getCompanyOptionsForEmploymentType(type).map((o) => ({ ...o }));
  const raw = String(currentCompanyName || '').trim();
  const current = normalizeEmployingCompanyName(raw);
  if (!raw) return options;
  if (options.some((o) => o.value === current)) return options;
  const known = COMPANY_OPTIONS.find((c) => c.value === current);
  if (known) {
    return [{ label: known.label, value: known.value, legacy: true }, ...options];
  }
  if (raw !== current) {
    return [{ label: `${current} — was "${raw}"`, value: current, legacy: true }, ...options];
  }
  return [
    { label: `${current} — custom`, value: current, legacy: true },
    ...options
  ];
}

export { EMPLOYMENT_TYPE_OPTIONS };
